import { Timer } from '@visiba/std';
import { PointerEvent, TouchEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { FileTypes, resolveFileType } from '../../../utils/file_type';
import { useText } from '../../../utils/hooks/use_text';
import { VisuallyHidden } from '../../visually_hidden';
import { FileViewerContext } from '../_file_viewer.context';
import type { FileWorkerStaleDuckType } from '../_file_worker_stale_duck_type';
import { FileViewerItemNoPreview } from './_file_viewer_list_item_no_preview.component';

const elements = {
	itemStyle: styled.div`
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		scroll-snap-align: start;
		flex-direction: column;
		overflow: hidden;
		will-change: transform;
		touch-action: manipulation;
		padding: 0 ${coreTokens.spacing.x1_5};
		outline: none;
	`,
	video: styled.video`
		width: auto;
		max-width: 100%;
		height: auto;
		max-height: 100%;

		&::-webkit-media-controls-fullscreen-button {
			display: none;
		}
	`,
	img: styled.img`
		user-select: none;
		-webkit-user-drag: none;
		-khtml-user-drag: none;
		-moz-user-drag: none;
		-o-user-drag: none;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	`,
};

interface FileViewerItemProps {
	file: FileWorkerStaleDuckType;
	index: number;
}

export function FileViewerItem({ index, file }: FileViewerItemProps): JSX.Element {
	const shouldDisableZoom = useRef(false);
	const canMutateBar = useRef(false);
	const canMutateBarAnimationTimer = useMemo(() => new Timer(), []);
	const { setHasDisabledScroll, setShowBarManually, currentIndex, files } = useContext(FileViewerContext);
	const ref = useRef<HTMLDivElement>(null);
	const staticTexts = useText();
	const [focusSet, setFocusSet] = useState(false);

	function handlePointerMove(event: PointerEvent<HTMLDivElement>): void {
		if (shouldDisableZoom.current) {
			event.preventDefault();
			event.stopPropagation();
		}

		canMutateBar.current = false;
	}

	function handleTouchStart(event: TouchEvent<HTMLDivElement>): void {
		setHasDisabledScroll((shouldDisableZoom.current = event.touches.length > 1));

		canMutateBar.current = true;
	}

	function handleTouchEnd(event: TouchEvent<HTMLDivElement>): void {
		setHasDisabledScroll((shouldDisableZoom.current = event.touches.length > 1));

		canMutateBarAnimationTimer.flush();

		canMutateBarAnimationTimer.repeat(() => {
			if (canMutateBar.current) {
				setShowBarManually((x) => !x);

				canMutateBarAnimationTimer.flush();
			}
		}, 300);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(() => {
		if (currentIndex === index && ref.current && !focusSet) {
			ref.current.focus();
			setFocusSet(true);
		}
	}, [currentIndex, ref, index, focusSet]);

	return (
		<elements.itemStyle
			data-index={index}
			onPointerMove={handlePointerMove}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
			ref={ref}
			tabIndex={-1}
			aria-hidden={currentIndex !== index}
		>
			<VisuallyHidden>
				{staticTexts('Component.FileViewer.FileCounter.Label', { currentFile: `${index + 1}`, totalFiles: `${files.length}` })}
				{'. '}
				{file.description ? file.description + '. ' : null}
				{file.fileName}
			</VisuallyHidden>
			<div aria-hidden='true' style={{ display: 'contents' }}>
				<DisplayedItem file={file} />
			</div>
		</elements.itemStyle>
	);
}

function DisplayedItem({ file }: { file: FileWorkerStaleDuckType }): JSX.Element {
	const resolvingFileType = file.fileType === '' ? file.fileName : file.fileType;
	const filetype = resolveFileType(resolvingFileType);

	if (filetype === FileTypes.Video) {
		return (
			<elements.video
				src={file.downloadUrl}
				controls
				muted={true}
				controlsList='nodownload nofullscreen noremoteplayback noplaybackrate'
				disablePictureInPicture
				disableRemotePlayback
				preload='auto'
			/>
		);
	}

	if (file.downloadUrl != null && filetype === FileTypes.Image) {
		return <elements.img src={file.downloadUrl} loading='lazy' alt='' />;
	}

	return <FileViewerItemNoPreview file={file} />;
}
